// src/pages/ToDoListPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useUser } from "../../contexts/UserContext";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import useScrollToTop from "../../hooks/useScrollToTop";
import Button from "./../../components/Button/Button";
import ButtonEdit from "./../../components/Button/ButtonEdit";
import ButtonDelete from "./../../components/Button/ButtonDelete";
import InfoBubble from "./../../components/InfoBubble/InfoBubble";
import PageHeader from "./../../components/Layout/PageHeader";
import { ToastContainer, toast } from "react-toastify";
import {
  getAllTasks,
  deleteTask,
  updateTask,
} from "../../services/tachesServices";
import { getAllUsers } from "../../services/userServices";
import "react-toastify/dist/ReactToastify.css";
import AddTaskModal from "./AddTaskModal";
import EditTaskModal from "./EditTaskModal";

/* ========================
   Statuts & styles Kanban
   ======================== */

const STATUS_ORDER = ["Urgent", "En cours", "A faire", "Effectué"];

const STATUS_META = {
  Urgent: {
    col: "bg-rose-50 dark:bg-rose-950/10",
    head: "text-rose-700 dark:text-rose-300",
    ring: "ring-rose-200 dark:ring-rose-800/50",
    badge: "bg-rose-600 text-white",
    accent: "bg-rose-600",
  },
  "En cours": {
    col: "bg-sky-50 dark:bg-sky-950/10",
    head: "text-sky-700 dark:text-sky-300",
    ring: "ring-sky-200 dark:ring-sky-800/50",
    badge: "bg-sky-600 text-white",
    accent: "bg-sky-600",
  },
  "A faire": {
    col: "bg-amber-50 dark:bg-amber-950/10",
    head: "text-amber-700 dark:text-amber-300",
    ring: "ring-amber-200 dark:ring-amber-800/50",
    badge: "bg-amber-500 text-slate-900",
    accent: "bg-amber-500",
  },
  Effectué: {
    col: "bg-emerald-50 dark:bg-emerald-950/10",
    head: "text-emerald-700 dark:text-emerald-300",
    ring: "ring-emerald-200 dark:ring-emerald-800/50",
    badge: "bg-emerald-600 text-white",
    accent: "bg-emerald-600",
  },
};

/* ================
   TaskCard (carte)
   ================ */

function TaskCard({ task, onEdit, onDelete, onChangeStatus }) {
  const meta = STATUS_META[task.statut] || STATUS_META["A faire"];
  const user = task.user || {};
  const avatar = user.photoURL || "/img/user_avatar.jpg";

  const handleDragStart = (e) => {
    e.dataTransfer.setData(
      "text/plain",
      JSON.stringify({ id: task.id, from: task.statut })
    );
    e.dataTransfer.effectAllowed = "move";
  };

  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // URL complète, data URL, blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) {
      return s;
    }
    // assets du front : on laisse
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  return (
    <article
      className={`relative rounded-xl border bg-white/95 dark:bg-slate-900/50 ${meta.ring} p-4 shadow-sm hover:shadow-md transition`}
      draggable
      onDragStart={handleDragStart}
      role="article"
      aria-label={task.titre || "Tâche"}
    >
      {/* liséré de statut à gauche */}
      <span
        className={`absolute left-0 top-0 bottom-0 w-[10px] rounded-l-xl ${meta.accent}`}
        aria-hidden="true"
      />

      {/* HEADER : avatar + nom/prénom */}
      <header className="flex items-start gap-3">
        <img
          src={toAbsolute(avatar)}
          alt={`${user.prenom || ""} ${user.nom || ""}`}
          className="h-11 w-11 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
            {(user.nom || "").toUpperCase()} {user.prenom || ""}
          </div>
          {task.categorie && (
            <div className="mt-1 inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] text-slate-700 dark:text-slate-300 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
              {task.categorie}
            </div>
          )}
        </div>
      </header>

      {/* TITRE */}
      {task.titre && (
        <h4 className="mt-3 text-[15px] font-semibold leading-snug text-slate-900 dark:text-white">
          {task.titre}
        </h4>
      )}

      {/* CONTENU */}
      <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
        <div className="max-h-28 overflow-hidden">
          <div dangerouslySetInnerHTML={{ __html: task.contenu }} />
        </div>
      </div>

      {/* STATUT + select */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 items-center">
        <span
          className={`inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${meta.badge}`}
        >
          {task.statut || "—"}
        </span>

        <div className="sm:justify-self-end">
          <select
            value={task.statut || ""}
            onChange={(e) => onChangeStatus(task, e.target.value)}
            className="w-full sm:w-auto rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs px-2.5 py-1.5 text-slate-800 dark:text-slate-100"
          >
            {Object.keys(STATUS_META).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="mt-4 flex items-center justify-end gap-2 pt-3 border-t border-slate-200/70 dark:border-slate-800/70">
        <ButtonEdit onClick={() => onEdit(task)} />
        <ButtonDelete onClick={() => onDelete(task.id)} />
      </div>
    </article>
  );
}

/* ==================
   Page ToDoList Comité
   ================== */

const ToDoListPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);

  const [statusFilter, setStatusFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);

  const [dragOver, setDragOver] = useState(""); // "Urgent" | "En cours" | ...

  useEffect(() => {
    (async () => {
      try {
        const [t, u] = await Promise.all([getAllTasks(), getAllUsers()]);
        setTasks(Array.isArray(t) ? t : []);
        setUsers(Array.isArray(u) ? u : []);
      } catch (error) {
        console.error("Chargement ToDoList :", error);
        toast.error("Erreur lors du chargement des données");
      }
    })();
  }, []);

  // Filtres appliqués
  const filteredTasks = useMemo(() => {
    let list = tasks.slice();
    if (statusFilter) list = list.filter((t) => t.statut === statusFilter);
    if (userFilter)
      list = list.filter((t) => String(t.user_id) === String(userFilter));
    return list;
  }, [tasks, statusFilter, userFilter]);

  // Groupement par statut
  const grouped = useMemo(() => {
    const map = new Map();
    STATUS_ORDER.forEach((s) => map.set(s, []));
    filteredTasks.forEach((t) => {
      map.has(t.statut)
        ? map.get(t.statut).push(t)
        : map.get("A faire").push(t);
    });
    return map;
  }, [filteredTasks]);

  // Compteurs
  const counters = useMemo(() => {
    const c = {};
    STATUS_ORDER.forEach((s) => (c[s] = 0));
    filteredTasks.forEach((t) => {
      c[t.statut] = (c[t.statut] || 0) + 1;
    });
    return c;
  }, [filteredTasks]);

  // Persist statut -> API
  const persistStatus = async (taskId, newStatus) => {
    await updateTask(taskId, { statut: newStatus });
  };

  const changeTaskStatus = async (task, newStatus) => {
    if (!task || task.statut === newStatus) return;
    const prev = tasks;
    // Optimiste
    setTasks((cur) =>
      cur.map((t) => (t.id === task.id ? { ...t, statut: newStatus } : t))
    );
    try {
      await persistStatus(task.id, newStatus);
      toast.success(`Statut mis à jour : ${newStatus}`);
    } catch (e) {
      console.error("MAJ statut:", e);
      setTasks(prev);
      toast.error("Impossible de mettre à jour le statut.");
    }
  };

  // Drag & drop
  const onDragOver = (e, status) => {
    e.preventDefault();
    if (dragOver !== status) setDragOver(status);
  };
  const onDragEnter = (e, status) => {
    e.preventDefault();
    setDragOver(status);
  };
  const onDragLeave = (e, status) => {
    if (dragOver === status) setDragOver("");
  };
  const onDrop = async (e, toStatus) => {
    e.preventDefault();
    setDragOver("");
    try {
      const str = e.dataTransfer.getData("text/plain");
      const data = JSON.parse(str || "{}");
      if (!data?.id) return;
      const task = tasks.find((t) => t.id === data.id);
      if (!task || task.statut === toStatus) return;
      await changeTaskStatus(task, toStatus);
    } catch (err) {
      console.error("Drop error:", err);
    }
  };

  // Actions
  const handleAddClick = () => setShowAddModal(true);

  const refreshTasks = async () => {
    try {
      const data = await getAllTasks();
      setTasks(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Erreur lors du rafraîchissement des tâches");
    }
  };

  const handleEditClick = (task) => {
    setCurrentTask(task);
    setShowEditModal(true);
  };

  const handleDeleteClick = async (taskId) => {
    try {
      await deleteTask(taskId);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.success("Tâche supprimée avec succès");
    } catch (error) {
      console.error("Suppression tâche :", error);
      toast.error("Erreur lors de la suppression de la tâche");
    }
  };

  return (
    <div className="flex flex-col w-full mx-auto min-h-screen mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] px-2 pb-10 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50">
      {/* Header unifié */}
      <PageHeader
        eyebrow="Membres du comité"
        title="ToDoList du comité"
        subtitle="Visualisez et organisez les tâches du comité : priorités, affectations et suivi d’avancement. Glissez-déposez les cartes entre colonnes ou changez le statut directement depuis la carte."
        align="center"
        size="md"
      />

      <div className="w-full max-w-7xl mx-auto">
        <InfoBubble title="Organisation des tâches">
          <p>
            Utilisez les filtres par statut et par membre pour vous concentrer
            sur un périmètre précis. Le glisser-déposer vous permet de
            réorganiser rapidement les priorités.
          </p>
        </InfoBubble>

        <div className="mt-4 rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau gradient / filtres / compteurs */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Kanban des tâches du comité
              </h2>
              <p className="mt-1 text-center text-sm text-white/90">
                Glissez-déposez les cartes entre colonnes ou changez le statut
                depuis la carte.
              </p>

              {/* Filtres + action */}
              <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-xl border border-white/40 bg-white/90 text-slate-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                >
                  <option value="">Tous statuts</option>
                  {STATUS_ORDER.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>

                <select
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="rounded-xl border border-white/40 bg-white/90 text-slate-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                >
                  <option value="">Tous utilisateurs</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {(u.nom || "").toUpperCase()} {u.prenom || ""}
                    </option>
                  ))}
                </select>

                <Button
                  text="+ Ajouter une tâche"
                  onClick={handleAddClick}
                  className="!rounded-xl !shadow-md"
                />
              </div>

              {/* Compteurs */}
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-sm">
                {STATUS_ORDER.map((s) => {
                  const meta = STATUS_META[s];
                  return (
                    <div
                      key={s}
                      className={`inline-flex items-center gap-2 rounded-xl px-3 py-1 ring-1 ${meta.ring} bg-white/90 text-slate-900`}
                      title={`Tâches « ${s} »`}
                    >
                      <span
                        className={`h-2.5 w-2.5 rounded-full ${meta.badge}`}
                      />
                      <span className="font-medium">{s}</span>
                      <span className="text-slate-600">
                        ({counters[s] || 0})
                      </span>
                    </div>
                  );
                })}
                <div className="inline-flex items-center gap-2 rounded-xl px-3 py-1 ring-1 ring-white/50 bg-white/90 text-slate-900">
                  <span className="font-semibold">Total</span>
                  <span className="text-slate-600">
                    ({filteredTasks.length})
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Kanban */}
          <div className="px-5 pb-6 sm:px-8 mt-6">
            {filteredTasks.length === 0 ? (
              <div className="text-center py-16">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucune tâche
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Ajustez vos filtres ou créez une nouvelle tâche.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-[960px] grid grid-cols-4 gap-4">
                  {STATUS_ORDER.map((status) => {
                    const meta = STATUS_META[status];
                    const items = grouped.get(status) || [];
                    const isOver = dragOver === status;

                    return (
                      <div
                        key={status}
                        className={`rounded-2xl border ${meta.ring} ${
                          meta.col
                        } p-3 transition ${
                          isOver ? "ring-2 ring-violet-500" : ""
                        }`}
                        onDragOver={(e) => onDragOver(e, status)}
                        onDragEnter={(e) => onDragEnter(e, status)}
                        onDragLeave={(e) => onDragLeave(e, status)}
                        onDrop={(e) => onDrop(e, status)}
                        role="list"
                        aria-label={`Colonne ${status}`}
                      >
                        <div
                          className={`mb-2 px-2 text-sm font-semibold ${meta.head}`}
                        >
                          {status}{" "}
                          <span className="opacity-70">({items.length})</span>
                        </div>
                        <div className="space-y-3">
                          {items.map((t) => (
                            <TaskCard
                              key={t.id}
                              task={t}
                              onEdit={handleEditClick}
                              onDelete={handleDeleteClick}
                              onChangeStatus={changeTaskStatus}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <ToastContainer />
      </div>

      {/* Modales */}
      {showAddModal && (
        <AddTaskModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          refreshList={refreshTasks}
        />
      )}
      {showEditModal && (
        <EditTaskModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          task={currentTask}
          refreshList={refreshTasks}
        />
      )}
    </div>
  );
};

export default ToDoListPage;
